import { z } from 'zod';
import {
  HERO_KPI_LABEL_ENGAGEMENT_RATE,
  HERO_KPI_LABEL_MEMBERS_ENGAGED,
  HERO_KPI_LABEL_NEEDS_FULFILLED,
  HERO_KPI_LABEL_SATISFACTION_SCORE,
  type HeroKpiTile,
} from './heroKpiSnapshot.util';

/** Stable ids for the four hero cover KPIs. */
export const HERO_KPI_IDS = [
  'membersEngaged',
  'engagementRate',
  'needsFulfilled',
  'satisfactionScore',
] as const;

/** One of the four toggleable hero cover KPIs. */
export type HeroKpiId = (typeof HERO_KPI_IDS)[number];

/** Per-KPI visibility on the hero cover. Missing keys default to visible. */
export type HeroKpiVisibility = Record<HeroKpiId, boolean>;

/** Builder labels for hero KPI toggles. */
export const HERO_KPI_TOGGLE_LABELS: Record<HeroKpiId, string> = {
  membersEngaged: HERO_KPI_LABEL_MEMBERS_ENGAGED,
  engagementRate: HERO_KPI_LABEL_ENGAGEMENT_RATE,
  needsFulfilled: HERO_KPI_LABEL_NEEDS_FULFILLED,
  satisfactionScore: HERO_KPI_LABEL_SATISFACTION_SCORE,
};

/** All four cover KPIs visible. */
export const DEFAULT_HERO_KPI_VISIBILITY: HeroKpiVisibility = {
  membersEngaged: true,
  engagementRate: true,
  needsFulfilled: true,
  satisfactionScore: true,
};

/** Zod schema for a complete hero KPI visibility map. */
export const HeroKpiVisibilitySchema = z
  .object({
    membersEngaged: z.boolean(),
    engagementRate: z.boolean(),
    needsFulfilled: z.boolean(),
    satisfactionScore: z.boolean(),
  })
  .strict();

/** Zod schema for a partial visibility patch. */
export const PartialHeroKpiVisibilitySchema = HeroKpiVisibilitySchema.partial();

const LABEL_TO_HERO_KPI_ID: Array<{ needle: string; id: HeroKpiId }> = [
  { needle: 'members engaged', id: 'membersEngaged' },
  { needle: 'engaged members', id: 'membersEngaged' },
  { needle: 'engagement rate', id: 'engagementRate' },
  { needle: 'needs fulfilled', id: 'needsFulfilled' },
  { needle: 'satisfaction', id: 'satisfactionScore' },
];

/**
 * Maps a hero KPI tile label to its toggle id.
 * @param label - Tile label from snapshot JSON.
 * @returns Matching KPI id, or null when the label is unrecognized.
 */
export const heroKpiIdFromLabel = (label: string): HeroKpiId | null => {
  const key = label.trim().toLowerCase();
  const match = LABEL_TO_HERO_KPI_ID.find(entry => key.includes(entry.needle));
  return match?.id ?? null;
};

/**
 * Normalizes stored or partial JSON into a complete visibility map.
 * Unknown or missing keys default to visible so existing reports keep all four KPIs.
 * @param value - Stored jsonb or API payload.
 * @returns Complete visibility map.
 */
export const normalizeHeroKpiVisibility = (
  value: unknown,
): HeroKpiVisibility => {
  const parsed = PartialHeroKpiVisibilitySchema.safeParse(value);
  if (!parsed.success) {
    return { ...DEFAULT_HERO_KPI_VISIBILITY };
  }
  return {
    ...DEFAULT_HERO_KPI_VISIBILITY,
    ...parsed.data,
  };
};

/**
 * Merges a visibility patch onto the current map.
 * @param current - Stored or previously normalized visibility.
 * @param patch - Fields to overwrite.
 * @returns Complete visibility map.
 */
export const mergeHeroKpiVisibility = (
  current: unknown,
  patch: Partial<HeroKpiVisibility>,
): HeroKpiVisibility => ({
  ...normalizeHeroKpiVisibility(current),
  ...patch,
});

/**
 * Accumulates a partial visibility patch for a queued persist.
 * Only patched keys are included so overlapping PATCHes cannot restore
 * KPIs the author hid in an earlier toggle.
 * @param pending - Previously queued patch, if any.
 * @param patch - Keys from this toggle.
 * @returns Combined partial patch.
 */
export const accumulateHeroKpiVisibilityPatch = (
  pending: Partial<HeroKpiVisibility> | undefined,
  patch: Partial<HeroKpiVisibility>,
): Partial<HeroKpiVisibility> => ({
  ...pending,
  ...patch,
});

/**
 * Filters hero KPI tiles by the author's visibility toggles.
 * Unrecognized tile labels stay visible.
 * @param tiles - Snapshot KPI tiles.
 * @param visibility - Author visibility map.
 * @returns Tiles that should render on the cover.
 */
export const filterHeroKpiTiles = (
  tiles: HeroKpiTile[],
  visibility: unknown,
): HeroKpiTile[] => {
  const resolved = normalizeHeroKpiVisibility(visibility);
  return tiles.filter(tile => {
    const id = heroKpiIdFromLabel(tile.label);
    return id === null ? true : resolved[id];
  });
};
